import { cn } from "@/ui/foundation/lib/utils";
import { attributeBarTone } from "@/ui/components/manager/manager-ui-helpers";

interface AttributeScoreBarProps {
    label: string;
    value: number;
    uncertainty?: number;
    className?: string;
}

function clampScore(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round(value)));
}

function clampUncertainty(value: number | undefined): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(50, Math.round(value ?? 0)));
}

export function AttributeScoreBar({ label, value, uncertainty, className }: AttributeScoreBarProps) {
    const score = clampScore(value);
    const margin = clampUncertainty(uncertainty);
    const lower = clampScore(score - margin);
    const upper = clampScore(score + margin);

    return (
        <div className={cn("rounded-md border p-3", className)}>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{label}</span>
                <span>
                    {score}
                    {margin > 0 ? ` ±${margin}` : ""}
                </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="relative h-full w-full">
                    <div className={cn("h-full", attributeBarTone(score))} style={{ width: `${score}%` }} />
                    {margin > 0 ? (
                        <div
                            className="absolute inset-y-0 rounded-sm border border-foreground/35 bg-foreground/15"
                            style={{
                                left: `${lower}%`,
                                width: `${Math.max(1, upper - lower)}%`
                            }}
                            aria-label="Scout uncertainty error bar"
                        />
                    ) : null}
                    <div
                        className="absolute inset-y-0 w-px bg-foreground/70"
                        style={{
                            left: `${score}%`
                        }}
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>
    );
}
